import React, { useState } from "react";
import logoIcon from "../../assets/soundscaperLogo.png";
import { LuMousePointer2 } from "react-icons/lu";
import { LuPencil, LuLanguages, LuPlus } from "react-icons/lu";
import { RiDragMove2Line } from "react-icons/ri";
import { MdDeleteOutline } from "react-icons/md";
import { VscDebugStart } from "react-icons/vsc";
import { VscDebugStop } from "react-icons/vsc";
import styles from "./Navbar.module.css";
import { RouteAction, ToolType } from "../../Reducer";
import { PublishIssue, RouteState } from "../../api/edits/editRoute.responses";
import Menu from "./Menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "../ui/Dialog";
import { Button } from "../ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/Dropdown";
import AddLanguageForm from "./AddLanguageForm";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/Tooltip";
import IssueDialog from "./IssueDialog";

interface NavbarProps {
  stateRouteName: string;
  currentTool: ToolType;
  setCurrentTool: React.Dispatch<React.SetStateAction<ToolType>>;
  routeDispatch: React.Dispatch<RouteAction>;
  routeState: RouteState;
  currentLanguage: string;
  setCurrentLanguage: React.Dispatch<React.SetStateAction<string>>;
  stateSelected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
}
export const Navbar = ({
  stateRouteName,
  currentTool,
  setCurrentTool,
  routeDispatch,
  routeState,
  currentLanguage,
  setCurrentLanguage,
  stateSelected,
  setSelected,
}: NavbarProps) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [publishIssues, setPublishIssues] = useState<PublishIssue[]>([]);
  const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState(false);

  return (
    <TooltipProvider>
    <>
    <IssueDialog isIssuesDialogOpen={isIssuesDialogOpen} setIsIssuesDialogOpen={setIsIssuesDialogOpen} publishIssues={publishIssues} />
    <nav className={styles.navbar}>
      <div className={styles.toolsContainer}>
        <Menu
          currentLanguage={currentLanguage}
          routeState={routeState}
          routeDispatch={routeDispatch}
          setCurrentLanguage={setCurrentLanguage}
          stateSelected={stateSelected}
          setSelected={setSelected}
          setPublishIssues={setPublishIssues}
          setIsIssuesDialogOpen={setIsIssuesDialogOpen}
        />

        <Tooltip>
          <TooltipTrigger>
            <LuMousePointer2
              className={
                currentTool === "select" ? styles.iconSelected : styles.icon
              }
              onClick={() => setCurrentTool("select")}
            />
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Select <kbd className="ml-1.5 rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">V</kbd>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger>
            <RiDragMove2Line
              className={currentTool === "move" ? styles.iconSelected : styles.icon}
              onClick={() => setCurrentTool("move")}
            />
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Move <kbd className="ml-1.5 rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">G</kbd>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger>
            <LuPencil
              className={currentTool === "draw" ? styles.iconSelected : styles.icon}
              onClick={() => setCurrentTool("draw")}
            />
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Draw <kbd className="ml-1.5 rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">B</kbd>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger>
            <MdDeleteOutline
              className={
                currentTool === "delete" ? styles.iconSelected : styles.icon
              }
              onClick={() => setCurrentTool("delete")}
            />
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Delete <kbd className="ml-1.5 rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">D</kbd>
          </TooltipContent>
        </Tooltip>

        <div className={styles.debugContainer}>
          <Tooltip>
            <TooltipTrigger>
              <VscDebugStart
                className={
                  currentTool === "start" ? styles.iconSelected : styles.icon
                }
                onClick={() => setCurrentTool("start")}
              />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Start walker <kbd className="ml-1.5 rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">R</kbd>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger>
              <VscDebugStop
                className={
                  currentTool === "stop" ? styles.iconSelected : styles.icon
                }
                onClick={() => setCurrentTool("stop")}
              />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Stop walker <kbd className="ml-1.5 rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">Esc</kbd>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      <a className={styles.title} href="/">
        {stateRouteName}
      </a>
      <div className={styles.langAndLogoContainer}>
        <div className={styles.langContainer}>
          <label className={styles.languageLabel}>{currentLanguage}</label>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button>
                <LuLanguages className={styles.icon} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Select language</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {Object.keys(routeState.routeTranslations).map((key) => {
                const languageTranslation = routeState.routeTranslations[key];
                return (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => setCurrentLanguage(key)}
                  >
                    {languageTranslation.languageName}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuItem onClick={() => setIsFormOpen(true)}>
                <LuPlus />
                Add
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <a className={styles.logoContainer} href="https://soundscaper.hr/">
          <img className={styles.logo} src={logoIcon} alt="Soundscaper logo" />
        </a>
      </div>

    </nav>
      <AddLanguageForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        routeDispatch={routeDispatch}
        routeState={routeState}
        setCurrentLanguage={setCurrentLanguage}
      />
    </>
    </TooltipProvider>
  );
};
